import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api, appUserAuth } from "../lib/api";
import { progressEvent } from "../lib/progress";

const STATES = ["Georgia", "Alabama", "Iowa", "Maine", "Maryland", "North Carolina", "Ohio", "Pennsylvania", "South Carolina", "Texas", "Virginia", "Other"];
const GOALS = [["retirement_income", "Retirement income"], ["protection", "Protection"], ["rollover", "Rollover"], ["legacy", "Legacy"]];
const TIMELINES = [["0-6", "0–6 months"], ["6-18", "6–18 months"], ["18+", "18+ months"]];
import { useVisitorProfile } from "../components/VisitorBanner";
import { ShieldCheck, ArrowRight, Lock, VideoCamera, Certificate, ArrowsClockwise, Sparkle } from "@phosphor-icons/react";

export default function Reserve() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tier = params.get("tier") || "standard";
  const visitor = useVisitorProfile();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: appUserAuth.fullName() || "", email: appUserAuth.email() || "", phone: "", goal: "", state: "", primary_goal: "", timeline: "" });
  const [loading, setLoading] = useState(false);
  const [eligibility, setEligibility] = useState({ checked: false, eligible: false, quiz: null });

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    // Pre-fill from visitor profile / app account + auto-check eligibility
    const p = visitor.get();
    const em = p?.email || appUserAuth.email();
    if (em) {
      setForm((f) => ({ ...f, name: p?.name || f.name, email: em }));
      api.get(`/reserve/eligibility?email=${encodeURIComponent(em)}`)
        .then((r) => setEligibility({ checked: true, eligible: r.data.eligible, quiz: r.data.quiz }))
        .catch(() => setEligibility({ checked: true, eligible: false, quiz: null }));
    } else {
      setEligibility({ checked: true, eligible: false, quiz: null });
    }
    // eslint-disable-next-line
  }, []);

  // Re-check eligibility when email field changes (debounced via blur)
  const onEmailBlur = async () => {
    if (!form.email || !form.email.includes("@")) return;
    try {
      const r = await api.get(`/reserve/eligibility?email=${encodeURIComponent(form.email)}`);
      setEligibility({ checked: true, eligible: r.data.eligible, quiz: r.data.quiz });
    } catch { /* noop */ }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const [freeReward, setFreeReward] = useState(false);
  useEffect(() => {
    if (appUserAuth.isLoggedIn()) api.get("/app/progress").then((r) => setFreeReward(r.data.rewards?.free_booking_unlocked && !r.data.rewards?.free_booking_used)).catch(() => {});
  }, []);

  const redeemFree = async () => {
    if (!form.state) return toast.error("Please select your state.");
    setLoading(true);
    try {
      const r = await api.post("/app/rewards/free-booking", { state: form.state, primary_goal: form.primary_goal || null, timeline: form.timeline || null, phone: form.phone || null, notes: form.goal || null });
      toast.success("Your free Illustration Review is reserved — pick a time with Leon.");
      setFreeReward(false);
      if (r.data.calendar_url) window.location.href = r.data.calendar_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not redeem your reward.");
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (freeReward) return redeemFree();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    if (!form.state) {
      toast.error("Please select your state.");
      return;
    }
    if (!eligibility.eligible) {
      toast.error("Please complete the 4-minute Find My Fit quiz first.");
      navigate("/quiz");
      return;
    }
    setLoading(true);
    progressEvent("book_click");
    try {
      const goalText = tier === "concierge" ? `[CONCIERGE] ${form.goal || ""}`.trim() : form.goal;
      const r = await api.post("/reserve/checkout", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        goal: goalText,
        state: form.state,
        primary_goal: form.primary_goal || null,
        timeline: form.timeline || null,
        origin_url: window.location.origin,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (err) {
      if (err?.response?.status === 412) {
        toast.error("Please complete the Find My Fit quiz first.");
        navigate("/quiz");
      } else {
        console.error(err);
        toast.error("Couldn't create checkout. Try again.");
      }
      setLoading(false);
    }
  };

  const amount = settings?.consult_deposit_amount ?? 100;

  return (
    <div data-testid="reserve-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />
      <section className="max-w-5xl mx-auto px-6 lg:px-12 pt-36 pb-16 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <p className="eyebrow"><span className="gold-divider" />Book a call</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[0.95]">
            {tier === "concierge" ? (
              <>The <span className="gold-text italic">Concierge</span> Audit.</>
            ) : (
              <>Your <span className="gold-text italic">Illustration Review</span> with Leon.</>
            )}
          </h1>
          <p className="text-exodus-muted mt-8 leading-relaxed max-w-md">
            {tier === "concierge"
              ? "A deep, multi-instrument strategy audit for high earners, business owners, and families with $250k+ in investable assets."
              : "Walk through your specific situation with Leon, The Structured Advisor. No pitch — a clear, written assessment of where you are and what could move next."}
          </p>

          {tier !== "concierge" && (
            <dl data-testid="reserve-expectations" className="mt-8 border-l-2 border-exodus-gold pl-5 max-w-md space-y-3 text-sm">
              {[
                ["What this is", "A private illustration review with Leon."],
                ["What you'll leave with", "Real numbers + a clear next step."],
                ["Time", "15–25 minutes."],
                ["Fee", freeReward ? "Free — your 12-check-in streak reward covers it." : "$100 refundable booking fee (applied to your time and refunded if it's not a fit)."],
                ["Tone", "No pressure. No quotas."],
              ].map(([k, v]) => (
                <div key={k}><dt className="eyebrow text-[10px]">{k}</dt><dd className="text-exodus-text/90 mt-0.5">{v}</dd></div>
              ))}
            </dl>
          )}

          <div className="card-surface mt-10 p-7 space-y-5">
            <div className="flex gap-3">
              <ShieldCheck size={22} weight="thin" className="text-exodus-gold mt-1 shrink-0" />
              <div>
                <p className="font-display text-lg">{freeReward ? "No deposit — streak reward" : `$${amount.toFixed(2)} refundable deposit`}</p>
                <p className="text-sm text-exodus-muted mt-1 leading-relaxed">
                  {freeReward ? "Your consistency earned this one. Your review with Leon is on us." : "The deposit secures Leon's calendar block. If the strategy isn't right for you — or you decide not to move forward — the deposit is refunded in full after the session."}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Lock size={22} weight="thin" className="text-exodus-gold mt-1 shrink-0" />
              <div>
                <p className="font-display text-lg">Private &amp; confidential</p>
                <p className="text-sm text-exodus-muted mt-1 leading-relaxed">
                  Your details are never shared, sold, or added to a drip campaign. This is a one-on-one conversation, not a funnel.
                </p>
              </div>
            </div>
          </div>
        </div>

        <form data-testid="reserve-form" onSubmit={submit} className="card-surface p-8 lg:p-10 space-y-5">
          <p className="eyebrow">Step 01 — Tell us who's joining</p>
          {freeReward && (
            <div data-testid="reserve-free-reward" className="border border-exodus-gold bg-exodus-gold/10 p-5">
              <p className="eyebrow">Streak reward unlocked</p>
              <p className="text-sm mt-2">Your 12-check-in streak earned a <span className="gold-text">free Illustration Review</span> — no $100 booking fee. Just choose your state below and book.</p>
            </div>
          )}

          {/* Eligibility banner */}
          {eligibility.checked && !eligibility.eligible && !freeReward && (
            <div data-testid="reserve-quiz-required" className="border border-exodus-gold bg-exodus-elevated p-5 flex items-start gap-3">
              <Sparkle size={20} weight="fill" className="text-exodus-gold flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Quiz required before reserving</p>
                <p className="text-xs text-exodus-muted mt-1 leading-relaxed">
                  Complete the 4-minute Find My Fit assessment so Leon walks into the call with your full context. The system remembers you — once you've taken it, you're recognized forever.
                </p>
                <Link
                  to="/quiz"
                  data-testid="reserve-go-to-quiz"
                  className="inline-flex items-center gap-2 mt-3 text-xs gold-text hover:underline"
                >
                  Take the 4-minute quiz <ArrowRight size={12} weight="bold" />
                </Link>
              </div>
            </div>
          )}
          {eligibility.checked && eligibility.eligible && eligibility.quiz && (
            <div data-testid="reserve-quiz-on-file" className="border border-exodus-emerald/40 bg-exodus-surface p-5 flex items-start gap-3">
              <Certificate size={20} className="text-exodus-emerald flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Quiz on file — you're good to reserve</p>
                <p className="text-xs text-exodus-muted mt-1">
                  Match: <span className="text-exodus-text">{eligibility.quiz?.recommendation?.primary_label}</span>
                </p>
              </div>
            </div>
          )}

          <input
            data-testid="reserve-input-name"
            className="input-exodus"
            placeholder="Full name"
            value={form.name}
            onChange={update("name")}
            required
          />
          <input
            data-testid="reserve-input-email"
            type="email"
            className="input-exodus"
            placeholder="Email address"
            value={form.email}
            onChange={update("email")}
            onBlur={onEmailBlur}
            required
          />
          <input
            data-testid="reserve-input-phone"
            type="tel"
            className="input-exodus"
            placeholder="Phone (optional)"
            value={form.phone}
            onChange={update("phone")}
          />
          <div data-testid="reserve-precall" className="border border-exodus-border p-5 space-y-3">
            <p className="eyebrow text-[10px]">Pre-call packet — state required, the rest recommended so Leon can prepare real numbers</p>
            <select data-testid="reserve-input-state" className="input-exodus" value={form.state} onChange={update("state")}>
              <option value="">State of residence *</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select data-testid="reserve-input-primary-goal" className="input-exodus" value={form.primary_goal} onChange={update("primary_goal")}>
              <option value="">Primary goal</option>
              {GOALS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select data-testid="reserve-input-timeline" className="input-exodus" value={form.timeline} onChange={update("timeline")}>
              <option value="">Timeline</option>
              {TIMELINES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <textarea
            data-testid="reserve-input-goal"
            className="input-exodus"
            placeholder="In one sentence — what would make this session feel valuable?"
            rows={3}
            value={form.goal}
            onChange={update("goal")}
          />
          <button
            data-testid="reserve-submit-button"
            type="submit"
            disabled={loading || (!eligibility.eligible && !freeReward)}
            className="btn-gold w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? "Redirecting…" : freeReward ? "Book my free Illustration Review" : !eligibility.eligible ? "Complete quiz first to unlock" : `Book my Illustration Review · $${amount.toFixed(0)} refundable`}
            <ArrowRight size={16} weight="bold" />
          </button>
          <p className="text-xs text-exodus-muted text-center leading-relaxed">
            {freeReward
              ? "No payment needed — you'll go straight to Leon's calendar to pick a time."
              : eligibility.eligible
              ? "You'll be taken to a secure Stripe checkout. After payment, you'll receive a receipt, your Google Meet link, and a booking link."
              : "Quiz required so Leon can prepare for your specific situation before the call."}
          </p>
        </form>
      </section>

      {/* Trust strip — license + Google Meet + refund + carriers */}
      <section data-testid="reserve-trust-strip" className="max-w-5xl mx-auto px-6 lg:px-12 pb-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-exodus-border border border-exodus-border">
          <TrustItem
            icon={<Certificate size={18} className="text-exodus-gold" />}
            title="Licensed in 11 states"
            sub="GA · AL · IA · ME · MD · NC · OH · PA · SC · TX · VA"
            linkTo="/credentials"
            testid="trust-licensed"
          />
          <TrustItem
            icon={<VideoCamera size={18} className="text-exodus-gold" />}
            title="Google Meet"
            sub="15–25 min review · link after booking"
            testid="trust-meet"
          />
          <TrustItem
            icon={<ArrowsClockwise size={18} className="text-exodus-gold" />}
            title="$100 fully refundable"
            sub="If the strategy isn't right, you get it back in full"
            testid="trust-refund"
          />
          <TrustItem
            icon={<ShieldCheck size={18} className="text-exodus-gold" />}
            title="A-rated carriers only"
            sub="Allianz · Pacific Life · MassMutual · Athene · Nationwide"
            testid="trust-carriers"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TrustItem({ icon, title, sub, linkTo, testid }) {
  const inner = (
    <div data-testid={testid} className="bg-exodus-surface p-6 h-full flex flex-col gap-2">
      <div className="flex items-center gap-2">{icon}<p className="font-medium text-sm">{title}</p></div>
      <p className="text-xs text-exodus-muted leading-relaxed">{sub}</p>
    </div>
  );
  return linkTo ? <Link to={linkTo} className="hover:bg-exodus-elevated transition-colors block">{inner}</Link> : inner;
}
